



























































































































































































































import type { JSONSchema7 } from 'json-schema';

import {
  defineComponent, PropType, ref, computed, nextTick,
} from '@vue/composition-api';

import jsYaml from 'js-yaml';

import VJsf from '@koumoul/vjsf/lib/VJsf';
import '@koumoul/vjsf/lib/deps/third-party';
import '@koumoul/vjsf/lib/VJsf.css';

import { publishRest } from '@/rest';
import { DandiModel, isJSONSchema } from '@/utils/schema/types';
import { EditorInterface } from '@/utils/schema/editor';
import MeditorTransactionTracker from '@/utils/transactions';

function renderField(fieldSchema: JSONSchema7) {
  const { properties } = fieldSchema;

  if (fieldSchema.readOnly) { return false; }
  const allSubPropsReadOnly = properties !== undefined && Object.keys(properties).every(
    (key) => {
      const subProp = properties[key];
      return isJSONSchema(subProp) && subProp.readOnly;
    },
  );

  if (allSubPropsReadOnly) { return false; }
  return true;
}

export default defineComponent({
  name: 'Meditor',
  components: { VJsf },
  props: {
    schema: {
      type: Object as PropType<JSONSchema7>,
      required: true,
    },
    model: {
      type: Object as PropType<DandiModel>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, ctx) {
    // TODO: Replace once direct-vuex is added
    const store = ctx.root.$store;

    const { model: modelProp, schema: schemaProp } = props;
    const invalidPermissionSnackbar = ref(false);

    const editorInterface = new EditorInterface(schemaProp, modelProp);
    const {
      modelValid,
      basicSchema,
      basicModel,
      basicModelValid,
      complexSchema,
      complexModel,
      setComplexModelProp,
      complexModelValid,
      complexModelValidation,
    } = editorInterface;

    const closeEditor = () => { ctx.emit('close'); };

    function sectionButtonColor(propKey: string) {
      const sectionValid = complexModelValidation[propKey];
      if (sectionValid !== undefined && !sectionValid) {
        return 'error';
      }

      return undefined;
    }

    const CommonVJSFOptions = computed(() => ({
      initialValidation: 'all',
      autoFixArrayItems: false,
      disableAll: props.readonly,
    }));
    const publishDandiset = computed(() => store.state.dandiset.publishDandiset);
    const id = computed(() => publishDandiset.value?.dandiset.identifier || null);

    const basicRef: any = ref(null);
    const complexRef: any = ref(null);

    const TransactionTracker = new MeditorTransactionTracker(editorInterface);

    const undoChange = () => {
      // Undo the change and then trigger revalidation of the form. The return value of undo()
      // indicates whether this was a basic or complex form change.
      if (TransactionTracker.undo()) {
        nextTick().then(() => complexRef.value.forEach((formRef: any) => formRef.validate()));
      } else {
        nextTick().then(() => basicRef.value.validate());
      }
    };

    const redoChange = () => {
      // Redo the change and then trigger revalidation of the form. The return value of redo()
      // indicates whether this was a basic or complex form change.
      if (TransactionTracker.redo()) {
        nextTick().then(() => complexRef.value.forEach((formRef: any) => formRef.validate()));
      } else {
        nextTick().then(() => basicRef.value.validate());
      }
    };

    const disableUndo = computed(
      () => props.readonly || !TransactionTracker.areTransactionsBehind(),
    );
    const disableRedo = computed(
      () => props.readonly || !TransactionTracker.areTransactionsAhead(),
    );

    const basicFormListener = () => TransactionTracker.add(basicModel.value, false);
    const complexFormListener = () => TransactionTracker.add(complexModel, true);

    const modified = computed(() => TransactionTracker.isModified());

    const exiting = ref(false);
    const exitMeditor = () => {
      if (modified.value) {
        exiting.value = true;
        return;
      }
      closeEditor();
    };

    async function save() {
      const dandiset = editorInterface.getModel();

      try {
        const { status, data } = await publishRest.saveDandiset(
          id.value, publishDandiset.value.version, dandiset,
        );

        if (status === 200) {
          // wait 0.5 seconds to give the celery worker some time to finish validation
          setTimeout(async () => {
            await store.dispatch('dandiset/fetchPublishDandiset', { identifier: data.dandiset.identifier, version: data.version });
            TransactionTracker.reset();
          }, 500);
        }
      } catch (error) {
        if (error.response.status === 403) {
          invalidPermissionSnackbar.value = true;
        }

        throw error;
      }
    }

    // TODO: Add back UI to toggle YAML vs JSON
    const yamlOutput = ref(false);
    const contentType = computed(() => (yamlOutput.value ? 'text/yaml' : 'application/json'));
    const output = computed(() => {
      const model = editorInterface.getModel();
      return yamlOutput.value ? jsYaml.dump(model) : JSON.stringify(model, null, 2);
    });

    function download() {
      const blob = new Blob([output.value], { type: contentType.value });

      const extension = contentType.value.split('/')[1];
      const filename = `dandiset.${extension}`;
      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    }

    return {
      allModelsValid: modelValid,

      basicSchema,
      basicModel,
      basicModelValid,
      basicRef,
      basicFormListener,

      complexSchema,
      complexModel,
      complexModelValid,
      complexModelValidation,
      complexRef,
      complexFormListener,

      invalidPermissionSnackbar,
      renderField,
      closeEditor,
      save,
      download,
      sectionButtonColor,

      modified,
      exiting,
      exitMeditor,

      undoChange,
      redoChange,
      disableUndo,
      disableRedo,

      CommonVJSFOptions,

      setComplexModelProp,

      TransactionTracker,
    };
  },
});
